import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Check,
  ArrowRight,
  Camera,
  BookOpen,
  Crown,
  Lock,
  CreditCard,
  Loader2,
  ImageIcon,
  Lightbulb,
  ClipboardList,
} from "lucide-react";
import { PRODUCTS } from "@/lib/products";
import { EBOOK, openEbook, openIntake, track } from "@/lib/ebook";
import { startCheckout } from "@/lib/payments";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FAQ from "@/components/FAQ";
import SEO from "@/components/SEO";
import PhotoSubmissionForm from "@/components/PhotoSubmissionForm";
import { SITE_URL } from "@/lib/seo";

const SEO_META = {
  "royal-essence": {
    title: "Royal Essence | Custom Portrait Photography & Artwork",
    description:
      "The complete Crowned experience — photography, story, creative direction and custom artwork. Royal Essence by Crowned Canvas Creations, Madison, Wisconsin.",
  },
  sovereign: {
    title: "Sovereign Canvas | Turn Your Photo Into Custom Artwork",
    description:
      "Already have the perfect photograph? Sovereign Canvas transforms your meaningful photo into personalized custom artwork. Order online from anywhere.",
  },
  "majestic-vibes": {
    title: "Majestic Vibes | Personalized Statement & Creative Wall Art",
    description:
      "No photograph required. Majestic Vibes turns your idea, aesthetic or vision into one-of-one custom artwork by Crowned Canvas Creations.",
  },
};

export default function ProductPage() {
  const { slug } = useParams();
  const product = PRODUCTS[slug];
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (PRODUCTS[slug]) {
      track(`${slug.replace(/-/g, "_")}_viewed`, "product-page");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) return <Navigate to="/" replace />;

  const meta = SEO_META[slug];
  const useSubmissionFlow = slug === "sovereign";

  const handlePay = async () => {
    setPaying(true);
    try {
      await startCheckout(product.checkoutPackageId, { source: `product-${slug}` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setPaying(false);
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "The Experiences", item: `${SITE_URL}/#experiences` },
        { "@type": "ListItem", position: 3, name: product.shortName, item: `${SITE_URL}/collection/${slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: product.fullName,
      description: `${product.positioning[0]} Finished physical piece: 16" × 20" framed canvas.`,
      provider: {
        "@type": "Organization",
        name: "Crowned Canvas Creations LLC",
        url: SITE_URL,
      },
      areaServed: slug === "royal-essence"
        ? { "@type": "City", name: "Madison, Wisconsin" }
        : { "@type": "Country", name: "United States" },
      offers: {
        "@type": "Offer",
        price: product.pricing.total,
        priceCurrency: "USD",
        description: `Total experience price $${product.pricing.total}. Begins with a $${product.pricing.initial} initial payment; remaining $${product.pricing.remaining} due ${product.pricing.due.toLowerCase()}`,
      },
    },
  ];

  return (
    <div className="relative">
      <SEO title={meta.title} description={meta.description} path={`/collection/${slug}`} jsonLd={jsonLd} />
      <Header />
      <main className="pt-24 md:pt-28">
        {/* ============ CINEMATIC HERO ============ */}
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_20%,rgba(130,73,55,0.3),transparent_65%)]" aria-hidden />
          <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p className="overline text-[#D4AF37]">{product.badge}</p>
              <h1 className="font-serif-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] mt-5 tracking-tight">
                {product.fullName}
              </h1>
              <p className="font-serif-display italic text-xl md:text-2xl text-[#D4AF37] mt-5 leading-snug">
                {product.headline}
              </p>
              {/* Emotional introduction */}
              <div className="space-y-4 mt-6 max-w-xl">
                {product.positioning.map((p, i) => (
                  <p key={i} className="text-white/70 font-light leading-relaxed text-base md:text-lg">
                    {p}
                  </p>
                ))}
              </div>

              {/* PRICING — finished piece / total / due today / remaining / when due / what happens next */}
              <div className="mt-8 border border-white/10 bg-[#111111] p-6" data-testid="pricing-block">
                <p className="overline text-[#D4AF37]">Investment</p>
                <dl className="mt-4 divide-y divide-white/5 text-sm">
                  <div className="flex justify-between gap-4 py-2.5">
                    <dt className="text-white/55">Finished piece</dt>
                    <dd className="text-white/90 text-right font-medium" data-testid="price-piece">{product.finishedPiece}</dd>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <dt className="text-white/55">Total experience price</dt>
                    <dd className="font-serif-display text-lg text-white" data-testid="price-total">${product.pricing.total}</dd>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <dt className="text-white/55">Initial payment due today</dt>
                    <dd className="font-serif-display text-lg text-[#D4AF37]" data-testid="price-initial">${product.pricing.initial}</dd>
                  </div>
                  <div className="flex justify-between py-2.5">
                    <dt className="text-white/55">Remaining balance</dt>
                    <dd className="font-serif-display text-lg text-white" data-testid="price-remaining">${product.pricing.remaining}</dd>
                  </div>
                  <div className="flex justify-between py-2.5 gap-4">
                    <dt className="text-white/55">When the balance is due</dt>
                    <dd className="text-white/70 text-xs text-right max-w-[240px]">{product.pricing.due}</dd>
                  </div>
                </dl>
                <p className="text-white/55 text-xs mt-4 border-t border-white/5 pt-4">
                  <span className="text-[#D4AF37]">What happens after payment:</span> your initial
                  payment officially begins your experience. You&apos;ll complete your Crowned
                  Creative Questionnaire and tell us your story.
                </p>
                <p className="text-white/40 text-[11px] mt-2">
                  Your initial payment is not the full experience price.
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mt-8">
                {useSubmissionFlow ? (
                  <a
                    href="#start-order"
                    data-testid="product-primary-cta"
                    className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
                  >
                    {product.ctaLabel} — ${product.pricing.initial} Today
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={handlePay}
                    disabled={paying}
                    data-testid="product-primary-cta"
                    className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50"
                  >
                    {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> {product.ctaLabel} — ${product.pricing.initial} Today</>}
                  </button>
                )}
                {slug === "sovereign" ? (
                  <button
                    type="button"
                    onClick={() => openEbook("product-sovereign-hero")}
                    data-testid="product-photo-guide-cta"
                    className="inline-flex items-center gap-3 border border-[#824937] bg-[#824937]/20 text-[#F7F1E7] px-7 py-4 text-xs uppercase tracking-[0.22em] hover:bg-[#824937]/40 transition-all"
                  >
                    <BookOpen className="w-4 h-4" />
                    Check My Photo First
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openIntake(`product-${slug}`)}
                    data-testid="product-intake-cta"
                    className="inline-flex items-center gap-3 border border-[#824937] bg-[#824937]/20 text-[#F7F1E7] px-7 py-4 text-xs uppercase tracking-[0.22em] hover:bg-[#824937]/40 transition-all"
                  >
                    <ClipboardList className="w-4 h-4" />
                    Start Your Crowned Intake
                  </button>
                )}
              </div>
              <p className="text-xs text-white/40 mt-5 inline-flex items-center gap-2">
                <Lock className="w-3 h-3" />
                Secure checkout by Stripe — no charges until you confirm.
              </p>
            </div>

            {/* Visual */}
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-[#824937]/30 blur-3xl group-hover:bg-[#824937]/50 transition-colors" aria-hidden />
                {product.image ? (
                  <div className="relative aspect-[4/5] border border-[#D4AF37]/30 overflow-hidden">
                    <img src={product.image} alt={product.imageAlt} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute top-5 right-5 overline bg-[#D4AF37] text-black px-3 py-1.5">
                      {product.badge}
                    </div>
                    {slug === "royal-essence" && (
                      <p className="absolute bottom-4 inset-x-4 text-center text-[11px] uppercase tracking-[0.18em] text-white/75">
                        A Crowned-designed artwork — finished-artwork direction
                      </p>
                    )}
                  </div>
                ) : (
                  <SovereignFrameVisual />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ============ WHO THIS IS FOR + WHAT MAKES IT DIFFERENT ============ */}
        <section className="py-16 md:py-24 border-t border-white/5 bg-black/40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-12 md:gap-16">
            <div>
              <div className="flex items-center gap-3">
                <Crown className="w-4 h-4 text-[#D4AF37]" />
                <p className="overline">Who This Is For</p>
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-5 leading-tight">
                This experience is yours if…
              </h2>
              <ul className="mt-6 space-y-3.5">
                {product.whoFor.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-base text-white/80 font-light">
                    <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                <p className="overline">The Difference</p>
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl mt-5 leading-tight">
                {product.differentTitle}
              </h2>
              <ul className="mt-6 space-y-3.5">
                {product.different.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-base text-white/80 font-light">
                    <span className="text-[#D4AF37] mt-1">✦</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ============ EXPERIENCE-SPECIFIC SECTION ============ */}
        {slug === "royal-essence" && (
          <section className="py-16 md:py-24 border-t border-white/5">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
              <div className="flex items-center gap-3">
                <Camera className="w-4 h-4 text-[#D4AF37]" />
                <p className="overline">The Photography Experience</p>
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-5 leading-tight max-w-3xl">
                Photography created with your <span className="italic text-[#D4AF37]">finished masterpiece</span> in mind.
              </h2>
              <p className="text-white/65 mt-6 font-light leading-relaxed max-w-2xl text-base md:text-lg">
                {product.photographyNote}
              </p>
            </div>
          </section>
        )}

        {slug === "sovereign" && (
          <section className="py-16 md:py-24 border-t border-white/5">
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-12 md:gap-16">
              <div>
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                  <p className="overline">Photo Requirements</p>
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl mt-5 leading-tight">
                  What makes a photo <span className="italic text-[#D4AF37]">canvas-ready.</span>
                </h2>
                <ul className="mt-6 space-y-3.5">
                  {product.photoRequirements.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-base text-white/80 font-light">
                      <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Prominent Perfect Photo Guide callout */}
              <div className="border border-[#D4AF37]/30 bg-[#824937]/15 p-7 md:p-10 self-center">
                <p className="overline text-[#D4AF37]">Not Sure Your Photo Is Ready?</p>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-4 leading-tight">
                  Check it against the free Crowned Perfect Photo Guide.
                </h3>
                <p className="text-white/65 mt-3 font-light text-sm md:text-base leading-relaxed">
                  The right photograph gives your masterpiece a stronger foundation.
                  Use the free Crowned Perfect Photo Guide before submitting your image.
                </p>
                <a
                  href={EBOOK.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("ebook_click", "sovereign-photo-guide")}
                  data-testid="sovereign-photo-guide-btn"
                  className="mt-6 inline-flex items-center gap-3 bg-[#D4AF37] text-black px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all"
                >
                  Check My Photo First
                  <ArrowUpRight className="w-4 h-4" />
                </a>
                <p className="mt-4">
                  <Link to="/photo-guide" data-testid="sovereign-photo-standards" className="text-xs uppercase tracking-[0.22em] text-white/55 hover:text-[#D4AF37] transition-colors">
                    Or read our photo standards →
                  </Link>
                </p>
              </div>
            </div>
          </section>
        )}

        {slug === "majestic-vibes" && (
          <section className="py-16 md:py-24 border-t border-white/5">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
              <div className="flex items-center gap-3">
                <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                <p className="overline">The Inspiration Process</p>
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-5 leading-tight max-w-3xl">
                You provide the inspiration. <span className="italic text-[#D4AF37]">Crowned creates the vibe.</span>
              </h2>
              <p className="text-white/65 mt-6 font-light leading-relaxed max-w-2xl text-base md:text-lg">
                {product.inspirationNote}
              </p>
            </div>
          </section>
        )}

        {/* ============ SOVEREIGN PROCESS VIDEO — real Crowned creation footage ============ */}
        {slug === "sovereign" && <SovereignVideo />}

        {/* ============ WHAT'S INCLUDED ============ */}
        <section className="py-16 md:py-24 border-t border-white/5 bg-black/40">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="flex items-center gap-3">
              <Check className="w-4 h-4 text-[#D4AF37]" />
              <p className="overline">What&apos;s Included</p>
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-5 leading-tight">
              Every detail handled.
            </h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {product.includes.map((f) => (
                <li key={f} className="flex items-start gap-3 text-base text-white/85 font-light">
                  <Check className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============ YOUR FINISHED CROWNED PIECE ============ */}
        <section data-testid="finished-piece-section" className="py-16 md:py-24 border-t border-white/5">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="overline">Your Finished Crowned Piece</p>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-[1.05]">
                One experience.<br />
                One <span className="italic text-[#D4AF37]">16&Prime; &times; 20&Prime; framed canvas.</span>
              </h2>
              <p className="text-white/65 mt-6 font-light leading-relaxed text-base md:text-lg">
                Every Crowned experience — Royal Essence, Sovereign Canvas or
                Majestic Vibes — is a different creative path to the same
                finished physical piece: a 16&Prime; &times; 20&Prime; framed
                canvas, produced only after your final approval and delivered
                to your door.
              </p>
            </div>
            <div className="border border-[#D4AF37]/30 bg-[#111111] p-8 text-center">
              <p className="overline text-white/50">Finished Physical Format</p>
              <p className="font-serif-display text-4xl md:text-5xl text-[#D4AF37] mt-4" data-testid="finished-piece-size">
                16&Prime; &times; 20&Prime;
              </p>
              <p className="text-white/70 mt-2">Framed Canvas</p>
              <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/50 uppercase tracking-[0.22em]">
                Proofed with you &middot; Two complimentary revision rounds &middot; Shipped to your door
              </div>
            </div>
          </div>
        </section>

        {/* ============ STEP-BY-STEP PROCESS ============ */}
        <section data-testid="product-timeline" className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid md:grid-cols-12 gap-10 mb-12">
              <div className="md:col-span-6">
                <p className="overline">Step-By-Step</p>
                <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05]">
                  From first payment to <span className="italic text-[#D4AF37]">your wall.</span>
                </h2>
              </div>
              <p className="md:col-span-5 md:col-start-8 text-white/60 font-light leading-relaxed self-end">
                Nothing rushed. Nothing generic. Every {product.shortName} piece
                moves through the same measured ritual — including your first
                proof and two complimentary revision rounds.
              </p>
            </div>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
              {product.process.map((s, i) => (
                <li
                  key={s.step}
                  data-testid={`product-step-${i + 1}`}
                  className="bg-[#080808] p-6 hover:bg-[#824937]/15 transition-colors"
                >
                  <span className="font-serif-display text-3xl text-[#D4AF37]/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif-display text-lg mt-4 leading-snug">{s.step}</h3>
                  <p className="text-white/55 text-sm mt-2 font-light leading-relaxed">{s.note}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ============ SOVEREIGN ORDER FLOW ============ */}
        {useSubmissionFlow && (
          <section id="start-order" data-testid="sovereign-start-order" className="py-20 md:py-28 border-t border-white/5 bg-black/40">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
              <div className="grid md:grid-cols-12 gap-10 mb-10">
                <div className="md:col-span-5">
                  <p className="overline">Start Your Order</p>
                  <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05]">
                    Your photo. Your story. <span className="italic text-[#D4AF37]">Your canvas.</span>
                  </h2>
                </div>
                <p className="md:col-span-6 md:col-start-7 text-white/60 font-light leading-relaxed self-end">
                  Four short steps — your details, your photo links, your vision,
                  and your $50.00 initial payment. Total experience: $149.99.
                </p>
              </div>
              <PhotoSubmissionForm
                packageId="sovereign"
                checkoutPackageId={product.checkoutPackageId}
                productName="Sovereign Canvas"
              />
            </div>
          </section>
        )}

        {/* ============ FAQ ============ */}
        <FAQ items={product.faqs} title="Questions, answered." testidPrefix={`product-${product.slug}-faq`} />

        {/* ============ FINAL CTA ============ */}
        <section className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-[900px] mx-auto px-6 text-center">
            <p className="overline">Ready?</p>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">
              Your story deserves <span className="italic text-[#D4AF37]">the Crowned treatment.</span>
            </h2>
            <p className="text-white/60 mt-5 font-light">
              Total experience ${product.pricing.total} — begin today with ${product.pricing.initial}.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-9">
              {useSubmissionFlow ? (
                <a href="#start-order" data-testid="product-bottom-cta" className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all">
                  {product.ctaLabel}<ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={paying}
                  data-testid="product-bottom-cta"
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{product.ctaLabel}<ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
              <Link
                to="/#experiences"
                className="inline-flex items-center gap-3 border border-white/20 text-white/85 px-8 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
              >
                Compare Experiences
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/** Sovereign hero visual — the customer brings the photo, so the visual is an
 *  empty Crowned frame awaiting their image (no stock photography). */
function SovereignFrameVisual() {
  return (
    <div className="relative aspect-[4/5] border border-[#D4AF37]/30 overflow-hidden bg-[#111111] flex items-center justify-center">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_35%,rgba(130,73,55,0.45),transparent_70%)]" aria-hidden />
      <div className="relative border border-[#D4AF37]/50 w-2/3 aspect-[3/4] flex flex-col items-center justify-center gap-4 bg-black/50 p-6 text-center">
        <ImageIcon className="w-10 h-10 text-[#D4AF37]/80" strokeWidth={1} />
        <p className="font-serif-display text-lg md:text-xl text-[#F7F1E7]/85 leading-snug">
          Your photograph<br />belongs here.
        </p>
        <p className="overline text-white/45">You bring the photo</p>
      </div>
      <div className="absolute top-5 right-5 overline bg-[#D4AF37] text-black px-3 py-1.5">
        Your Photograph, Crowned
      </div>
    </div>
  );
}

/** Real Crowned creation/process footage — actual behind-the-scenes
 *  transformation footage. Source photographs in the demo are NOT represented
 *  as paying customers. */
function SovereignVideo() {
  const onPlay = () => track("sovereign_video_played", "sovereign-page");
  return (
    <section data-testid="sovereign-video-section" className="py-16 md:py-24 border-t border-white/5 bg-black/40">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <p className="overline text-[#D4AF37]">See The Transformation</p>
          <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-[1.05]">
            From photograph to <span className="italic text-[#D4AF37]">Crowned masterpiece.</span>
          </h2>
          <p className="text-white/65 mt-6 font-light leading-relaxed text-base md:text-lg">
            Real Crowned creation footage — watch how an ordinary source
            photograph becomes the starting point for something far more
            personal, expressive and presentation-ready.
          </p>
          <p className="text-white/40 text-xs mt-4 uppercase tracking-[0.18em]">
            Crowned creative demonstration &middot; process footage
          </p>
        </div>
        <div className="md:col-span-6">
          <div className="relative border border-[#D4AF37]/30 overflow-hidden max-w-[420px] mx-auto">
            <video
              data-testid="sovereign-process-video"
              src="/sovereign-process.mp4"
              poster="/sovereign-process-poster.jpg"
              controls
              playsInline
              preload="none"
              onPlay={onPlay}
              aria-label="Crowned Canvas Creations — real Sovereign Canvas creation process footage"
              className="w-full h-auto block"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
